import { useEffect, useState } from 'react';
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { toast } from 'sonner';
import { Save } from 'lucide-react';
import type { SapEmploymentValues } from '@hcc/types';
import { Button } from '../../../../components/ui/Button';
import { EmploymentFieldsGrid } from '../../../../components/sap/EmploymentFieldsGrid';
import { api, ApiError } from '../../../../lib/api';

interface EmploymentResponse {
  effective: SapEmploymentValues;
  origins: Partial<Record<keyof SapEmploymentValues, string>>;
  override: SapEmploymentValues;
  role_defaults: SapEmploymentValues;
}

/**
 * The SAP employment record: HR decisions no onboarding form captures.
 * Role defaults fill anything not overridden per person; saving here
 * writes the person-level override.
 */
export function SapEmploymentCard({ personId }: { personId: string }) {
  const queryClient = useQueryClient();
  const employment = useQuery({
    queryKey: ['sap-employment', personId],
    queryFn: () => api<EmploymentResponse>(`/api/admin/sap/people/${personId}/employment`),
  });

  const [draft, setDraft] = useState<SapEmploymentValues>({});
  useEffect(() => {
    if (employment.data) setDraft(employment.data.effective);
  }, [employment.data]);

  const save = useMutation({
    mutationFn: () =>
      api(`/api/admin/sap/people/${personId}/employment`, {
        method: 'PATCH',
        body: JSON.stringify({
          org_centre_id: draft.org_centre_id ?? null,
          job_id: draft.job_id ?? null,
          contract_type: draft.contract_type ?? null,
          weekly_hours: draft.weekly_hours ?? null,
          working_time_model: draft.working_time_model ?? null,
        }),
      }),
    onSuccess: () => {
      toast.success('Employment record saved');
      void queryClient.invalidateQueries({ queryKey: ['sap-employment', personId] });
      void queryClient.invalidateQueries({ queryKey: ['sap-review', personId] });
    },
    onError: (err) => toast.error(err instanceof ApiError ? err.message : 'save failed'),
  });

  const hintFor = (key: keyof SapEmploymentValues): string | null => {
    const origin = employment.data?.origins?.[key];
    if (!origin) return null;
    return origin === 'employment_role' ? 'from role default' : 'person override';
  };

  return (
    <section className="overflow-hidden rounded-lg border border-line/10 bg-surface shadow-sm">
      <div className="flex items-center justify-between border-b border-line/10 px-5 py-3">
        <h3 className="text-sm font-semibold">SAP employment record</h3>
        <Button
          variant="secondary"
          size="sm"
          iconLeft={<Save className="h-4 w-4" />}
          loading={save.isPending}
          onClick={() => save.mutate()}
        >
          Save
        </Button>
      </div>
      <div className="px-5 py-4">
        <EmploymentFieldsGrid draft={draft} onChange={setDraft} hintFor={hintFor} />
      </div>
    </section>
  );
}
